import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onStartTour, markTourCompleted } from '../lib/onboarding';

/* Lightweight custom product tour — no external dependency. Steps target real
 * UI elements by [data-tour="..."]. A dimmed overlay with a cut-out spotlight
 * highlights the target; a tooltip explains it with Back/Next/Skip. Missing
 * targets are skipped gracefully. Triggered via the fti-start-tour event. */

interface TourStep {
  target: string;      // data-tour attribute value; '' = centered (no spotlight)
  title: string;
  body: string;
  goTo?: string;       // navigate here before showing the step
}

const STEPS: TourStep[] = [
  { target: '', title: 'Quick tour', body: 'A 60-second look at where things are. You can skip anytime and replay later from the “?” menu.' },
  { target: 'nav-sam-library', title: 'SAM.gov Library', body: 'Search 1.1M+ federal notices and awards. Filter by NAICS, PSC, set-aside, agency, place of performance, or awardee.', goTo: '/sam-library' },
  { target: 'nav-vendors', title: 'Vendors', body: 'Research the supplier base — capabilities, past awards (prime), and subcontracting history.', goTo: '/vendors' },
  { target: 'nav-price-benchmark', title: 'Price Benchmark', body: 'See what agencies paid for a NAICS/PSC and generate a grounded AI cost estimate (IGCE).', goTo: '/price-benchmark' },
  { target: 'nav-opportunities', title: 'Opportunities', body: 'Track a procurement through its lifecycle: Intake (FAR 11) → Acquisition (FAR 12–15) → Matching → Evaluation → Award.', goTo: '/opportunities' },
  { target: 'help-button', title: 'Help is always here', body: 'Replay this tour, reopen the welcome, or find the getting-started checklist from the “?” menu. Each stage also has its own guidance card.' },
];

const PAD = 8;

export default function GuidedTour() {
  const nav = useNavigate();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => onStartTour(() => { setIdx(0); setActive(true); }), []);

  const step = STEPS[idx];

  const locate = useCallback(() => {
    if (!step) return;
    if (!step.target) { setRect(null); return; }
    const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setRect(el.getBoundingClientRect());
    } else {
      setRect(null);
    }
  }, [step]);

  // When the step changes: navigate if needed, then locate the target (after paint).
  useEffect(() => {
    if (!active || !step) return;
    let t2: number;
    if (step.goTo) {
      nav(step.goTo);
      t2 = window.setTimeout(locate, 450);
    } else {
      t2 = window.setTimeout(locate, 100);
    }
    return () => window.clearTimeout(t2);
  }, [active, idx, step, nav, locate]);

  // Re-measure on resize/scroll while active.
  useEffect(() => {
    if (!active) return;
    const h = () => locate();
    window.addEventListener('resize', h);
    window.addEventListener('scroll', h, true);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('scroll', h, true); };
  }, [active, locate]);

  if (!active || !step) return null;

  const finish = () => { markTourCompleted(); setActive(false); };
  const next = () => { if (idx < STEPS.length - 1) setIdx(idx + 1); else finish(); };
  const back = () => { if (idx > 0) setIdx(idx - 1); };

  // Tooltip position: below the target if there's room, else centered.
  const vw = window.innerWidth, vh = window.innerHeight;
  const TW = 340;
  let tipStyle: React.CSSProperties;
  if (rect) {
    const below = rect.bottom + 12;
    const placeBelow = below + 160 < vh;
    const left = Math.min(Math.max(12, rect.left), vw - TW - 12);
    tipStyle = placeBelow
      ? { top: below, left }
      : { top: Math.max(12, rect.top - 172), left };
  } else {
    tipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000 }} aria-live="polite">
      {/* Dim overlay. When a target exists, draw a spotlight ring around it. */}
      <div
        onClick={() => { /* clicking the backdrop does nothing; use Skip */ }}
        style={{ position: 'absolute', inset: 0, background: rect ? 'transparent' : 'rgba(15,23,42,0.5)' }}
      />
      {rect && (
        <div style={{
          position: 'absolute',
          top: rect.top - PAD, left: rect.left - PAD,
          width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          borderRadius: 10,
          boxShadow: '0 0 0 9999px rgba(15,23,42,0.5)',
          border: '2px solid #818cf8',
          pointerEvents: 'none',
          transition: 'all 0.2s ease',
        }} />
      )}

      {/* Tooltip */}
      <div style={{
        position: 'absolute', width: TW, maxWidth: '92vw', background: 'white', borderRadius: 10,
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)', padding: 16, ...tipStyle,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>{step.title}</div>
        <div style={{ fontSize: 12.5, color: '#4b5563', marginTop: 6, lineHeight: 1.55 }}>{step.body}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <button onClick={finish} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer' }}>Skip</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{idx + 1} / {STEPS.length}</span>
            {idx > 0 && (
              <button onClick={back} style={{ padding: '6px 12px', background: 'white', color: '#4b5563', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Back</button>
            )}
            <button onClick={next} style={{ padding: '6px 14px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {idx === STEPS.length - 1 ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
